
import { supabase } from '@/integrations/supabase/client';
import { TweetSubmissionResult, TwitterLinkResult } from './types';

// Define authentication headers interface
interface AuthHeaders {
  [key: string]: string;
}

// Get Telegram WebApp initData for authentication
function getTelegramInitData(): string | null {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) {
    return window.Telegram.WebApp.initData;
  }
  return null;
}

// Submit tweet for engagement scoring using simplified validation
export async function submitTweetForScoring(userId: string, tweetUrl: string, isManual = true): Promise<TweetSubmissionResult> {
  try {
    console.log('Submitting tweet with simplified validation:', tweetUrl);
    
    // Get authentication headers
    const authHeaders: AuthHeaders = {};
    const telegramData = getTelegramInitData();
    if (telegramData) {
      authHeaders['x-telegram-data'] = telegramData;
      console.log('Added Telegram authentication data to tweet submission');
    }
    
    const { data: result, error } = await supabase.functions.invoke('twitter-api', {
      body: {
        action: 'submit_tweet',
        userId: userId,
        tweetUrl: tweetUrl,
        isManual: isManual
      },
      headers: authHeaders
    });

    if (error) {
      console.error('Error invoking twitter-api function:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit tweet'
      };
    }
    
    console.log('Tweet submission result:', result);

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || 'Failed to submit tweet'
      };
    }

    return {
      success: true,
      score: result.score
    };
  } catch (error) {
    console.error('Error submitting tweet:', error);
    return {
      success: false,
      error: 'Network error occurred'
    };
  }
}

// Link Twitter handle - Simplified version (no userId needed, auth determines user)
export async function linkTwitterHandle(twitterHandle: string): Promise<TwitterLinkResult> {
  try {
    console.log('Linking Twitter handle with simplified verification:', twitterHandle);
    
    // Get authentication headers
    const authHeaders: AuthHeaders = {};
    const telegramData = getTelegramInitData();
    if (telegramData) {
      authHeaders['x-telegram-data'] = telegramData;
      console.log('Added Telegram authentication data to Twitter handle linking');
    }
    
    const { data: result, error } = await supabase.functions.invoke('simple-twitter-verify', {
      body: {
        action: 'verify_handle',
        twitterHandle: twitterHandle
      },
      headers: authHeaders
    });

    if (error) {
      console.error('Error invoking simple-twitter-verify function:', error);
      return {
        success: false,
        error: error.message || 'Failed to link Twitter handle'
      };
    }
    
    console.log('Twitter handle linking result:', result);

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || 'Failed to link Twitter handle'
      };
    }

    return {
      success: true
    };
  } catch (error) {
    console.error('Error linking Twitter handle:', error);
    return { success: false, error: 'An error occurred while linking Twitter' };
  }
}

// Link Twitter handle by wallet address - for webpage onboarding
export async function linkTwitterHandleByWallet(walletAddress: string, twitterHandle: string): Promise<TwitterLinkResult> {
  try {
    console.log('Linking Twitter handle by wallet with simplified verification:', twitterHandle);
    
    // Get authentication headers
    const authHeaders: AuthHeaders = {};
    const telegramData = getTelegramInitData();
    if (telegramData) {
      authHeaders['x-telegram-data'] = telegramData;
      console.log('Added Telegram authentication data to wallet-based Twitter linking');
    }
    
    const { data: result, error } = await supabase.functions.invoke('simple-twitter-verify', {
      body: {
        action: 'verify_handle',
        walletAddress: walletAddress,
        twitterHandle: twitterHandle
      },
      headers: authHeaders
    });

    if (error) {
      console.error('Error invoking simple-twitter-verify function:', error);
      return {
        success: false,
        error: error.message || 'An error occurred while linking Twitter'
      };
    }
    
    console.log('Twitter handle linking by wallet result:', result);

    return result || { success: false, error: 'No result returned from function' };
  } catch (error) {
    console.error('Error linking Twitter handle by wallet:', error);
    return { success: false, error: 'An error occurred while linking Twitter' };
  }
}
